function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useRef, useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, useDevice, useOpenUnsavedChangesModal } from 'seagull';
import { routes } from "../../../constants";
import { useEventReporter, useOverlayContext, useStore, useStoreActions } from "../../../contexts";
import { useContactsMap } from "../../../hooks/reactQuery/derived/useContactsMap";
import { useFeatureFlags } from "../../../hooks/useFeatureFlags";
import { Intent } from "../../../infra/access-control";
import { handleRequest, notEmpty } from "../../../infra/utils";
import { proposalTemplateModel } from "../../../models";
import { fromDateStampObject, toDateStampObject } from "../../../models/time";
import { ClientPreview } from "../../ClientPreview/ClientPreview";
import { RelationshipEffectiveDate } from "../../RelationshipEffectiveDate/RelationshipEffectiveDate";
import { AgreementName } from "../AgreementName/AgreementName";
import AgreementStudioModal, { SubmitType } from "../AgreementStudioModal/AgreementStudioModal";
import { fromAgreementStudioCommon } from "../AgreementStudioPage.utils";
import { withAgreementStudioDeviceProvider } from "../withAgreementStudioDeviceProvider";
import { TemplateName } from "./TemplateName/TemplateName";
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
const saveTemplateLoader = 'saveTemplate';
export function AgreementStudioTemplateMode(props) {
  const {
    template
  } = props;
  const {
    isMobileDevice
  } = useDevice();
  const history = useHistory();
  const {
    api,
    logBI
  } = useStoreActions();
  const overlayContext = useOverlayContext();
  const openUnsavedChangesModal = useOpenUnsavedChangesModal();
  const legalTermsDocuments = useStore(state => state.legalTerms.data);
  const {
    reportButtonClick
  } = useEventReporter();
  const isEditingTemplate = template != null;
  const scrollToTopRef = useRef(null);
  const {
    START_BILLING_AT,
    START_BILLING_AT_PHASE_2_INLINE
  } = useFeatureFlags();
  const startBillingAtEnabled = START_BILLING_AT || START_BILLING_AT_PHASE_2_INLINE;
  const effectiveDateValue = useMemo(() => template !== null && template !== void 0 && template.effectiveDate ? fromDateStampObject(template.effectiveDate) : null, [template]);
  const [effectiveDate, setEffectiveDate] = useState(effectiveDateValue);
  const [templateName, setTemplateName] = useState(template === null || template === void 0 ? void 0 : template.title);
  const [agreementName, setAgreementName] = useState((template === null || template === void 0 ? void 0 : template.agreementName) || null);
  const [templateNameInvalid, setTemplateNameInvalid] = useState(false);
  const [agreementNameInvalid, setAgreementNameInvalid] = useState(false);
  const [showTemplateNameInvalid, setShowTemplateNameInvalid] = useState(false);
  const [showAgreementNameInvalid, setShowAgreementNameInvalid] = useState(false);
  const {
    contactsMap,
    sampleContact
  } = useContactsMap(props.worksAt.id);
  function goToProposalTemplates() {
    history.push(routes.proposalTemplates);
  }
  const onSubmit = async data => {
    logBI(isEditingTemplate ? 10493 : 10492);
    if (templateName == null || templateName === '' || templateNameInvalid) {
      setShowTemplateNameInvalid(true);
      if (scrollToTopRef.current != null) scrollToTopRef.current();
      return;
    }
    if (agreementNameInvalid) {
      setShowAgreementNameInvalid(true);
      if (scrollToTopRef.current != null) scrollToTopRef.current();
      return;
    }
    const templateToSave = fromAgreementStudioCommon(_objectSpread({
      data,
      title: templateName,
      agreementName: (agreementName === null || agreementName === void 0 ? void 0 : agreementName.trim()) || null
    }, startBillingAtEnabled ? {
      effectiveDate: effectiveDate ? toDateStampObject(effectiveDate) : null
    } : {
      effectiveDate: null
    }));
    overlayContext.startLoader(saveTemplateLoader);
    const result = await handleRequest(isEditingTemplate ? api.proposalTemplates.save(template.id, templateToSave) : api.proposalTemplates.create(templateToSave).then(() => {}));
    if (!result.success) {
      const {
        text
      } = result.error.createRelationshipErrorText();
      overlayContext.showErrorToast(text);
      overlayContext.stopLoader(saveTemplateLoader);
      return;
    }
    goToProposalTemplates();
    overlayContext.showSuccessToast('Proposal template successfully saved', 'proposal-template-saved-toast');
    overlayContext.stopLoader(saveTemplateLoader);
  };
  const templateToCommonWithId = template => {
    var _ref, _template$legalTermsD;
    return {
      id: template.id,
      coverLetter: template.coverLetter,
      extraClause: template.extraClause,
      requirePaymentMethod: template.requirePaymentMethod,
      allowManualPayments: template.allowManualPayments,
      isExistingCustomer: template.isExistingCustomer,
      customNotificationText: template.customNotificationText,
      services: template.services,
      serviceBundles: template.serviceBundles,
      netTerms: template.netTerms,
      legalTermsDocuments: (_ref = (_template$legalTermsD = template.legalTermsDocuments) === null || _template$legalTermsD === void 0 ? void 0 : _template$legalTermsD.filter(notEmpty)) !== null && _ref !== void 0 ? _ref : [],
      coverCreditCardFees: template.feeControls.coverCreditCardFees,
      /** Will automatic ongoing services be billed at the start or end of the billing-period. */
      amendmentsApprovalMethod: template.amendmentsApprovalMethod,
      invoicesApprovalMethod: template.invoicesApprovalMethod,
      requestAccountantAccess: template.requestAccountantAccess,
      introVideo: template.introVideo,
      expirationDate: template.expirationDate
    };
  };
  const openPreviewModal = data => {
    const draft = proposalTemplateModel.toPreviewData(proposalTemplateModel.fromDTO(fromAgreementStudioCommon(_objectSpread({
      data,
      title: templateName || '',
      agreementName: (agreementName === null || agreementName === void 0 ? void 0 : agreementName.trim()) || null
    }, startBillingAtEnabled ? {
      effectiveDate: effectiveDate ? toDateStampObject(effectiveDate) : null
    } : {
      effectiveDate: null
    }))));
    reportButtonClick('agreement-previewed-agreement');
    overlayContext.showModal({
      closeOnClickOutside: true,
      closeOnEscapeKey: true,
      content: modalProps => /*#__PURE__*/_jsx(ClientPreview, _objectSpread({
        type: "draft",
        draftInfo: {
          draft,
          sampleContact,
          contactsMap
        }
      }, modalProps))
    });
  };
  const ui = isEditingTemplate ? {
    modalProps: {
      draft: templateToCommonWithId(template),
      modalTitle: 'Edit proposal template'
    },
    buttonProps: {
      children: 'Save changes'
    }
  } : {
    modalProps: {
      draft: null,
      modalTitle: 'Create proposal template'
    },
    buttonProps: {
      children: 'Save template',
      intent: Intent.CreateProposalTemplate
    }
  };
  return /*#__PURE__*/_jsx(AgreementStudioModal, _objectSpread(_objectSpread(_objectSpread({}, props), ui.modalProps), {}, {
    legalTermsDocuments: legalTermsDocuments || [],
    effectiveDate: effectiveDate,
    agreementTitleMP: /*#__PURE__*/_jsxs(_Fragment, {
      children: [!isEditingTemplate && /*#__PURE__*/_jsx(TemplateName, {
        templateTitle: templateName || '',
        setTemplateTitle: setTemplateName,
        setTemplateTitleValid: value => setTemplateNameInvalid(!value),
        showWarning: showTemplateNameInvalid,
        label: "Template name",
        testid: "agreement-studio-customer-details-input-display-title"
      }), /*#__PURE__*/_jsx(AgreementName, {
        fullWidth: isMobileDevice,
        agreementTitle: agreementName || '',
        setAgreementTitle: setAgreementName,
        setAgreementTitleValid: value => setAgreementNameInvalid(!value),
        showWarning: showAgreementNameInvalid,
        label: "Agreement name",
        testid: "agreement-studio-customer-details-input-agreement-name",
        allowEmpty: true
      }), startBillingAtEnabled && /*#__PURE__*/_jsx(RelationshipEffectiveDate, {
        value: effectiveDate,
        disabled: false,
        onChange: setEffectiveDate,
        title: "Agreement effective date",
        description: "The date when you can start providing the services and bill the client."
      })]
    }),
    doScrollToTopRef: scrollToTopRef,
    onSubmitCb: onSubmit,
    shouldClose: hasUnsavedChanges => hasUnsavedChanges ? openUnsavedChangesModal({
      testid: 'warn-unsaved-changes-modal'
    }) : Promise.resolve(true),
    isTemplateProposal: true,
    onClose: goToProposalTemplates,
    additionalAutoSaveDependencies: [templateName, agreementName],
    openPreviewModal: openPreviewModal,
    renderSubmitButton: _ref2 => {
      let {
        testid,
        className,
        onSubmit
      } = _ref2;
      return /*#__PURE__*/_jsx(Button, _objectSpread({
        "data-testid": testid,
        className: className,
        onClick: () => onSubmit(SubmitType.SendProposal)
      }, ui.buttonProps));
    }
  }));
}
export const AgreementStudioTemplateModeWithMobileSupport = withAgreementStudioDeviceProvider(AgreementStudioTemplateMode);